/**
 * Tag-pill text color — twin of the Containd app helper `getContrastColor`
 * (containd: fullcomponents/edit-components/src/utils/colorContrast.ts).
 *
 * The published canvas must paint tag pills with the SAME font color the app
 * canvas uses, so this mirrors that helper's input handling (3-digit expand,
 * invalid → black) and its WCAG luminance result. Keep in sync; see
 * Policy/CanvasRenderParityWithSpace.
 */

export function relativeLuminance(r: number, g: number, b: number): number {
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

export function contrastRatio(luminanceA: number, luminanceB: number): number {
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Pick black or white — whichever yields higher WCAG contrast. */
export function contrastTextForBg(hex: string): string {
  const raw = hex.replace('#', '').trim();
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  if (full.length !== 6) return '#000000';
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((channel) => Number.isNaN(channel))) return '#000000';
  const backgroundLuminance = relativeLuminance(r, g, b);
  const whiteContrast = contrastRatio(1, backgroundLuminance);
  const blackContrast = contrastRatio(backgroundLuminance, 0);
  return whiteContrast >= blackContrast ? '#FFFFFF' : '#000000';
}
